'use client';

import { useEffect, useState } from 'react';
import { useBehaviorTracker } from '@/hooks/useBehaviorTracker';
import { getPersonalizedRecommendations, RecommendationsOutput } from '@/ai/flows/personalized-recommendations';
import { Sparkles, BrainCircuit, Search, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import Link from 'next/link';

const RECOMMENDATION_CACHE_KEY = 'ai_personalized_recommendations_cache_v1';
const RECOMMENDATION_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

export function AIPersonalizedRecommendations() {
    const { getHistoryString } = useBehaviorTracker();
    const [recommendation, setRecommendation] = useState<RecommendationsOutput | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function loadRecommendations() {
            try {
                const cachedRaw = localStorage.getItem(RECOMMENDATION_CACHE_KEY);
                if (cachedRaw) {
                    try {
                        const parsed = JSON.parse(cachedRaw) as { expiresAt: number; data: RecommendationsOutput };
                        if (parsed && parsed.expiresAt > Date.now() && parsed.data) {
                            setRecommendation(parsed.data);
                            setLoading(false);
                            return;
                        }
                    } catch {
                        localStorage.removeItem(RECOMMENDATION_CACHE_KEY);
                    }
                }

                const historyData = getHistoryString();
                // Always call it, even if history is empty, to get a general greeting
                const result = await getPersonalizedRecommendations({
                    historyString: historyData || 'لم يتصفح المستخدم أي شيء بعد. قدم تحية عامة وشجعه على البدء.'
                });
                setRecommendation(result);
                localStorage.setItem(
                    RECOMMENDATION_CACHE_KEY,
                    JSON.stringify({
                        expiresAt: Date.now() + RECOMMENDATION_CACHE_TTL_MS,
                        data: result,
                    })
                );
            } catch (error) {
                console.error('Failed to load AI recommendations:', error);
            } finally {
                setLoading(false);
            }
        }

        // Small delay to ensure localStorage is hydrated if running immediately on mount
        const timer = setTimeout(() => {
            loadRecommendations();
        }, 500);

        return () => clearTimeout(timer);
    }, []);

    if (loading) {
        return (
            <div className="w-full bg-gradient-to-r from-indigo-50 to-purple-50 rounded-[2rem] p-4 md:p-5 border border-indigo-100/50 shadow-sm mb-6 animate-pulse flex items-center gap-3">
                <div className="bg-indigo-100 p-3 rounded-xl">
                    <Loader2 className="h-5 w-5 text-indigo-500 animate-spin" />
                </div>
                <div>
                    <div className="h-3.5 bg-indigo-200 rounded w-40 mb-2"></div>
                    <div className="h-3 bg-indigo-100 rounded w-64"></div>
                </div>
            </div>
        );
    }

    if (!recommendation) return null;

    return (
        <div className="w-full bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#4338ca] rounded-[2rem] p-5 md:p-6 shadow-xl shadow-indigo-900/20 mb-6 relative overflow-hidden group">
            {/* Background Decorators */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500 rounded-full blur-3xl opacity-20 -mr-20 -mt-20 group-hover:opacity-30 transition-opacity duration-1000"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-500 rounded-full blur-3xl opacity-20 -ml-10 -mb-10 group-hover:translate-x-10 transition-transform duration-1000"></div>

            <div className="relative z-10 flex flex-col md:flex-row gap-4 md:gap-5 items-start justify-between">

                {/* Text Content */}
                <div className="flex-1 space-y-3">
                    <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/10 border border-white/20 backdrop-blur-md">
                        <Sparkles className="h-3.5 w-3.5 text-amber-300" />
                        <span className="text-xs font-bold text-indigo-50 tracking-wide">مستشارك الذكي</span>
                    </div>

                    <h2 className="text-xl md:text-[1.75rem] font-black text-white font-headline leading-snug">
                        {recommendation.greeting}
                    </h2>

                    <p className="text-indigo-100/90 font-medium leading-relaxed max-w-2xl text-xs md:text-sm">
                        <span className="text-amber-300 font-bold mr-1">نصيحة اليوم:</span>
                        {recommendation.advice}
                    </p>

                    <div className="pt-1">
                        <Link href={`/buy/results?q=${encodeURIComponent(recommendation.suggestedSearch)}`}>
                            <Button className="bg-white hover:bg-indigo-50 text-indigo-900 rounded-xl h-10 px-4 text-sm font-bold shadow-lg gap-2 group/btn">
                                <Search className="h-4 w-4 text-indigo-500" />
                                بحث: {recommendation.suggestedSearch}
                                <ArrowLeft className="h-4 w-4 opacity-50 group-hover/btn:-translate-x-1 transition-transform" />
                            </Button>
                        </Link>
                    </div>
                </div>

                {/* Icon/Visual */}
                <div className="hidden md:flex flex-col items-center justify-center shrink-0">
                    <div className="w-20 h-20 bg-white/10 backdrop-blur-md rounded-[1.5rem] border border-white/20 flex items-center justify-center shadow-2xl relative">
                        <div className="absolute inset-0 bg-gradient-to-tr from-indigo-500/20 to-purple-500/20 rounded-[1.5rem] animate-pulse"></div>
                        <BrainCircuit className="h-10 w-10 text-indigo-100 relative z-10" />
                    </div>
                </div>

            </div>
        </div>
    );
}
